import React from "react";

/**
 * Minimal, dependency-free markdown renderer for shop-authored copy
 * (installation T&Cs, customer-facing quote summaries, etc.).
 *
 * Supports the subset that actually appears in real shop content:
 *  - `# / ## / ###` headings
 *  - `- ` unordered lists and `1. ` ordered lists (single level)
 *  - `**bold**`, `*italic*`, `_italic_`
 *  - `---` horizontal rule
 *  - blank lines → paragraph break
 *
 * No HTML injection: bold/italic are parsed into React nodes via a
 * tiny regex tokenizer so we never call `dangerouslySetInnerHTML`.
 * Anything that doesn't match falls through as a plain paragraph so
 * shops can freely paste existing T&Cs without markdown quirks.
 */
export default function RenderMarkdown({ source, className = "" }) {
  const lines = (source || "").split("\n");
  const out = [];
  let bullets = [];
  let ordered = [];

  const flushBullets = (key) => {
    if (bullets.length) {
      out.push(
        <ul
          key={`ul-${key}`}
          className="list-disc list-outside pl-5 space-y-1 my-2 marker:text-emerald-400"
        >
          {bullets.map((b, i) => (
            <li key={i}>{renderInline(b)}</li>
          ))}
        </ul>
      );
      bullets = [];
    }
  };
  const flushOrdered = (key) => {
    if (ordered.length) {
      out.push(
        <ol
          key={`ol-${key}`}
          className="list-decimal list-outside pl-5 space-y-1 my-2 marker:text-emerald-400"
        >
          {ordered.map((b, i) => (
            <li key={i}>{renderInline(b)}</li>
          ))}
        </ol>
      );
      ordered = [];
    }
  };
  const flushAll = (key) => {
    flushBullets(key);
    flushOrdered(key);
  };

  lines.forEach((raw, idx) => {
    const l = raw.trim();
    if (l.startsWith("### ")) {
      flushAll(idx);
      out.push(<h3 key={idx} className="text-base text-white font-semibold mt-4 mb-1">{renderInline(l.slice(4))}</h3>);
      return;
    }
    if (l.startsWith("## ")) {
      flushAll(idx);
      out.push(<h2 key={idx} className="text-lg text-white font-semibold mt-5 mb-2">{renderInline(l.slice(3))}</h2>);
      return;
    }
    if (l.startsWith("# ")) {
      flushAll(idx);
      out.push(<h1 key={idx} className="text-xl text-white font-semibold mt-5 mb-2">{renderInline(l.slice(2))}</h1>);
      return;
    }
    if (l === "---" || l === "***") {
      flushAll(idx);
      out.push(<hr key={idx} className="border-t border-white/10 my-4" />);
      return;
    }
    if (l.startsWith("- ") || l.startsWith("* ")) {
      flushOrdered(idx);
      bullets.push(l.slice(2));
      return;
    }
    const orderedMatch = l.match(/^(\d+)\.\s+(.*)$/);
    if (orderedMatch) {
      flushBullets(idx);
      ordered.push(orderedMatch[2]);
      return;
    }
    flushAll(idx);
    if (!l) {
      out.push(<div key={idx} className="h-2" />);
      return;
    }
    out.push(<p key={idx} className="my-1">{renderInline(raw)}</p>);
  });
  flushAll("end");

  if (className) return <div className={className}>{out}</div>;
  return <>{out}</>;
}

/**
 * Tokenise a single line into React nodes so `**bold**` and `*italic*` /
 * `_italic_` markers render correctly. Anything unmatched passes through
 * as plain text.
 */
function renderInline(text) {
  if (!text) return null;
  // Match **bold**, *italic* or _italic_ — non-greedy, single line.
  const re = /(\*\*[^*]+\*\*|\*[^*\n]+\*|_[^_\n]+_)/g;
  const parts = String(text).split(re);
  return parts.map((part, i) => {
    if (!part) return null;
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("*") && part.endsWith("*")) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith("_") && part.endsWith("_")) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}
